import { billableAmount } from '../../../billing'
import type { Project } from '../types'

export interface CaseBilling {
  project: Project
  amount: number
}

// The biggest billable cases first, for the leaderboard next to the chart.
export const topCasesByAmount = (
  projects: Project[],
  limit: number,
): CaseBilling[] =>
  projects
    .map((project) => ({
      project,
      amount: billableAmount(project.numberOfPages),
    }))
    .sort((first, second) => second.amount - first.amount)
    .slice(0, limit)

// Billable amount by processed month, oldest first, for the trend chart. A case with no processed
// date falls back to its created date so a freshly processed case still lands on the timeline.
export const billableByMonth = (
  projects: Project[],
): Array<{ date: string; value: number }> => {
  const byMonth = new Map<string, { sortKey: number; amount: number }>()
  for (const project of projects) {
    const stamp = project.firstProcessedAt || project.createdAt
    if (!stamp) continue
    const date = new Date(stamp)
    if (Number.isNaN(date.getTime())) continue
    const label = date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    })
    const sortKey = date.getFullYear() * 12 + date.getMonth()
    const existing = byMonth.get(label) ?? { sortKey, amount: 0 }
    existing.amount += billableAmount(project.numberOfPages)
    byMonth.set(label, existing)
  }
  return Array.from(byMonth.entries())
    .sort(([, first], [, second]) => first.sortKey - second.sortKey)
    .map(([label, { amount }]) => ({ date: label, value: amount }))
}
